import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, X, FileText, Mail, Save, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import useShareTargets from '../../hooks/useShareTargets';
import { VISIBILITY_BADGE, VisibilitySelect, ShareTargets } from '../sharing/SharingPicker';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../lib/emailTemplateCategories';

// FR-013 — Email Templates sidebar for the Campaign Builder.
//
// Reuses the existing /api/email-templates infrastructure (same collection
// as Inbox, MeetingActionModal, ContactEmailComposer, SequenceEditor and
// Admin > Email Templates). Templates created from this sidebar are
// flagged `for_campaigns: true` so they don't pollute the sales-side
// pickers — that filtering happens server-side (default exclude unless
// `?include_campaign=1`).
//
// Scoping: respects `is_admin_template` from the existing model. Personal
// templates are user-private; admin templates are org-wide and require
// admin privileges to create / edit / delete.
//
// Batch 7b — 3-tier permission model. Each template now carries:
//   visibility: "private" | "shared" | "organization"
//   shared_with: [user_id, ...]   (the "shared" tier)
//   owner_name / user_id          (creator)
// The binary "Share with org" toggle becomes a Visibility select. Org-wide
// is admin-only and still routes through the admin path (is_admin_template).
// Private/Shared route through the personal path with visibility + shared_with.
// Templates the user doesn't own expose a Fork action
// (POST /email-templates/{id}/fork) instead of Edit/Delete. (#978 retired the
// never-enforced `locked` flag and its icon.)
//
// #955 — the visibility select and the people/groups pickers live in
// components/sharing/SharingPicker.jsx, shared with Settings → Templates &
// Signatures, so a "Shared" template can also be shared with groups here.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SCOPE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'Mine' },
    { key: 'org', label: 'Org' },
];

export default function EmailTemplatePanel({
    open,
    onClose,
    onInsert,
    canSaveFromCurrent,
    currentEmail,           // {subject, body} from selected email step
    currentStepLabel,
    isAdmin: isAdminProp,
    getAuthHeader: getAuthHeaderProp,
}) {
    // Prefer context values (gives us user.user_id for ownership checks); fall
    // back to props for backwards-compat with CampaignBuilder's call site.
    const { user, getAuthHeader: getAuthHeaderCtx } = useAuth();
    const getAuthHeader = getAuthHeaderProp || getAuthHeaderCtx;
    const isAdmin = typeof isAdminProp === 'boolean'
        ? isAdminProp
        : !!user?.is_admin;
    const currentUserId = user?.user_id;

    const [templates, setTemplates] = useState([]);     // user templates
    const [adminTemplates, setAdminTemplates] = useState([]); // org admin
    const { members, groups } = useShareTargets(!!open);  // org members + groups for shared-with
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState('all');
    const [showEditor, setShowEditor] = useState(false);
    const [editing, setEditing] = useState(null);  // null = create new
    const [form, setForm] = useState({ name: '', subject: '', body: '', category: 'general', visibility: 'private', shared_with: [], shared_with_groups: [] });
    const [saving, setSaving] = useState(false);
    const [forkingId, setForkingId] = useState(null);

    const load = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/email-templates?include_campaign=1`, { headers: getAuthHeader() });
            setTemplates(res.data.templates || []);
            setAdminTemplates(res.data.admin_templates || []);
        } catch (err) {
            toast.error('Failed to load templates');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [open, getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const openCreate = (preFromCurrent = false) => {
        setEditing(null);
        setForm({
            name: preFromCurrent && currentStepLabel ? currentStepLabel : '',
            subject: preFromCurrent ? (currentEmail?.subject || '') : '',
            body: preFromCurrent ? (currentEmail?.body || '') : '',
            category: 'general',
            visibility: 'private',
            shared_with: [],
            shared_with_groups: [],
        });
        setShowEditor(true);
    };

    const openEdit = (template) => {
        // Derive visibility: org masters (admin templates) map to "organization";
        // otherwise read the template's own visibility (legacy → "private").
        const vis = template.is_admin_template
            ? 'organization'
            : (template.visibility || 'private');
        setEditing(template);
        setForm({
            name: template.name || '',
            subject: template.subject || '',
            body: template.body || '',
            category: template.category || 'general',
            visibility: vis,
            shared_with: Array.isArray(template.shared_with) ? template.shared_with : [],
            shared_with_groups: Array.isArray(template.shared_with_groups) ? template.shared_with_groups : [],
        });
        setShowEditor(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
            return toast.error('Name, subject, and body are required');
        }
        setSaving(true);
        try {
            // Org-wide (admin-only) routes through the admin path (is_admin_template).
            // Private/Shared route through the personal path with visibility + shared_with.
            const isAdminTemplate = form.visibility === 'organization' && isAdmin;
            const baseUrl = isAdminTemplate ? `${API}/admin/email-templates` : `${API}/email-templates`;
            const payload = {
                name: form.name.trim(),
                subject: form.subject.trim(),
                body: form.body,
                category: form.category,
                for_campaigns: true,  // FR-013 — flag so sales-side hides it
            };
            if (!isAdminTemplate) {
                payload.visibility = form.visibility;
                payload.shared_with = form.visibility === 'shared' ? form.shared_with : [];
                payload.shared_with_groups = form.visibility === 'shared' ? form.shared_with_groups : [];
            }
            if (editing) {
                const url = isAdminTemplate ? `${baseUrl}/${editing.template_id}` : `${API}/email-templates/${editing.template_id}`;
                await axios.put(url, payload, { headers: getAuthHeader() });
                toast.success('Template updated');
            } else {
                await axios.post(baseUrl, payload, { headers: getAuthHeader() });
                toast.success('Template saved');
            }
            setShowEditor(false);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            toast.error(`Failed to save: ${detail}`);
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    // Fork any visible template into a private personal copy.
    const handleFork = async (template) => {
        setForkingId(template.template_id);
        try {
            await axios.post(`${API}/email-templates/${template.template_id}/fork`, {}, { headers: getAuthHeader() });
            toast.success(`Forked "${template.name}" to your private templates`);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            toast.error(`Failed to fork: ${detail}`);
            console.error(err);
        } finally {
            setForkingId(null);
        }
    };


    const handleDelete = async (template) => {
        if (!window.confirm(`Delete template "${template.name}"?`)) return;
        try {
            const url = template.is_admin_template
                ? `${API}/admin/email-templates/${template.template_id}`
                : `${API}/email-templates/${template.template_id}`;
            await axios.delete(url, { headers: getAuthHeader() });
            toast.success('Template deleted');
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Network error';
            toast.error(`Failed to delete: ${detail}`);
        }
    };

    // Filter + search
    const all = [
        ...templates.map(t => ({ ...t, _scope: 'mine' })),
        ...adminTemplates.map(t => ({ ...t, _scope: 'org' })),
    ];
    const filtered = all.filter(t => {
        if (scope !== 'all' && t._scope !== scope) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                (t.name || '').toLowerCase().includes(q) ||
                (t.subject || '').toLowerCase().includes(q) ||
                (t.category || '').toLowerCase().includes(q)
            );
        }
        return true;
    });

    if (!open) return null;

    return (
        <>
            <div className="w-80 bg-white border-l border-stone-200 flex flex-col flex-shrink-0">
                {/* Header */}
                <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-stone-600" />
                        <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Email Templates
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-700" title="Close templates panel">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Quick-save from current email */}
                {canSaveFromCurrent && (
                    <div className="px-4 py-2 border-b border-stone-100 bg-indigo-50/50">
                        <button onClick={() => openCreate(true)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                            <Save className="w-3.5 h-3.5" /> Save current email as template
                        </button>
                    </div>
                )}

                {/* New template button */}
                <div className="px-4 py-2 border-b border-stone-100">
                    <button onClick={() => openCreate(false)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> New blank template
                    </button>
                </div>

                {/* Search + filter */}
                <div className="px-4 py-2 border-b border-stone-100 space-y-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search templates..."
                            className="w-full pl-7 pr-2 py-1.5 text-xs border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-stone-400"
                        />
                    </div>
                    <div className="flex gap-1">
                        {SCOPE_FILTERS.map(f => (
                            <button key={f.key} onClick={() => setScope(f.key)}
                                className={`flex-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                                    scope === f.key ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto px-2 py-2">
                    {loading ? (
                        <p className="text-xs text-stone-400 text-center py-4">Loading…</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-xs text-stone-400 text-center py-4">No templates yet — click "New" to create one</p>
                    ) : (
                        <div className="space-y-1.5">
                            {filtered.map(t => {
                                const isOrgMaster = !!t.is_admin_template || t._scope === 'org';
                                const vis = isOrgMaster ? 'organization' : (t.visibility || 'private');
                                const badge = VISIBILITY_BADGE[vis] || VISIBILITY_BADGE.private;
                                const isOwner = !!currentUserId && t.user_id === currentUserId;
                                // Owner can always edit/delete their own. Admins can edit/delete
                                // org masters. Anything else the user doesn't own → Fork only.
                                const canManage = isOwner || (isOrgMaster && isAdmin);
                                const showFork = !canManage;
                                return (
                                <div key={t.template_id} className="group p-2.5 rounded-lg border border-stone-200 hover:border-indigo-300 hover:shadow-sm transition-all bg-white">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold text-stone-900 truncate">{t.name}</p>
                                            <p className="text-[11px] text-stone-500 truncate">{t.subject || '(no subject)'}</p>
                                            {t.owner_name && !isOwner && (
                                                <p className="text-[10px] text-stone-400 truncate">by {t.owner_name}</p>
                                            )}
                                        </div>
                                        <div className="shrink-0 flex items-center gap-1">
                                            <span title={badge.label}
                                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ${badge.cls}`}>
                                                {badge.label}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 mt-2">
                                        <button onClick={() => onInsert?.(t)}
                                            disabled={!onInsert}
                                            className="flex-1 px-2 py-1 text-[11px] font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            title={onInsert ? 'Insert into selected email step' : 'Select an email step to insert'}>
                                            Insert
                                        </button>
                                        {showFork ? (
                                            <button onClick={() => handleFork(t)}
                                                disabled={forkingId === t.template_id}
                                                className="p-1 text-stone-500 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                title="Fork to a private copy you can edit">
                                                <Copy className="w-3 h-3" />
                                            </button>
                                        ) : (
                                            <>
                                                <button onClick={() => openEdit(t)}
                                                    className="p-1 text-stone-500 hover:text-stone-900"
                                                    title="Edit template">
                                                    <Edit2 className="w-3 h-3" />
                                                </button>
                                                <button onClick={() => handleDelete(t)}
                                                    className="p-1 text-stone-500 hover:text-red-600"
                                                    title="Delete template">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer note */}
                <div className="px-4 py-2 border-t border-stone-100 text-[10px] text-stone-400">
                    Templates saved here only show in Campaign Builder, not the sales inbox.
                </div>
            </div>

            {/* Create / Edit Modal */}
            <Dialog open={showEditor} onOpenChange={setShowEditor}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5" />
                            {editing ? 'Edit template' : 'New template'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Name</label>
                            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Day-1 follow-up"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Category</label>
                                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                    {EMAIL_TEMPLATE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <VisibilitySelect
                                value={form.visibility}
                                onChange={visibility => setForm({ ...form, visibility })}
                                allowOrganization={isAdmin}
                            />
                        </div>
                        {/* Shared-with people + groups (only for the "Shared" tier) */}
                        <ShareTargets
                            visibility={form.visibility}
                            sharedWith={form.shared_with}
                            sharedWithGroups={form.shared_with_groups}
                            onChange={targets => setForm({ ...form, ...targets })}
                            members={members}
                            groups={groups}
                            currentUserId={currentUserId}
                            idPrefix="campaign-template-share"
                        />
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Subject</label>
                            <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                                placeholder="Email subject line..."
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Body (HTML)</label>
                            <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                                placeholder="<p>Hi {{FirstName}}, ...</p>"
                                rows={10}
                                className="w-full px-3 py-2 text-xs font-mono border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            <p className="text-[10px] text-stone-400 mt-1">
                                Use the email step's editor for visual formatting; this textarea accepts raw HTML.
                                Supported merge vars include {'{{FirstName}}'}, {'{{LastName}}'}, {'{{CompanyName}}'}, etc.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800">
                            {saving ? 'Saving…' : editing ? 'Update template' : 'Save template'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
